import { ClockCircleOutlined, LockOutlined } from '@ant-design/icons'
import { usersAPI } from '@/api'
import { licenseAndPaymentApi } from '@/api/queries/licenseAndPayment'
import { LicenseAndPaymentData } from '@/api/types/licenseAndPayment.types'
import { isError } from '@/helpers/checkQuery'
import { useAddAlert } from '@/hooks/useAddAlert'
import useRoles from '@/hooks/useRoles'
import { AuthService } from '@/services/AuthService'
import {
  isLicensePaymentModalDue,
  isLicensePaymentReminderDue,
} from '@/utils/licensePaymentStatus'
import Button from '@UI/New/Button'
import { fetchMenu } from '@store/menu.reducer'
import { actionSetProfile, actionUpdProfile } from '@store/profile.reducer'
import { Modal } from 'antd'
import moment from 'moment'
import { FC, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import classes from './index.module.css'
import { useLicensePaymentReminder } from './useLicensePaymentReminder'

interface LicenseExpiredModalProps {
  profile?: {
    id?: number | string
    role?: string
    roles?: string[]
  }
}

const LicenseExpiredModal: FC<LicenseExpiredModalProps> = ({ profile }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const addAlert = useAddAlert()
  const { isEmployee } = useRoles()
  const [data, setData] = useState<LicenseAndPaymentData>()
  const [isOpeningPayment, setIsOpeningPayment] = useState(false)
  const canManagePayments =
    profile?.role === 'admin' ||
    profile?.role === 'junior-admin' ||
    profile?.roles?.some?.((role) =>
      ['admin', 'junior-admin'].includes(role),
    ) === true
  const isLicensePage = location.pathname.startsWith('/license-and-payment')
  const { now, isSnoozed, remindLater, refreshKey } = useLicensePaymentReminder(
    data,
    profile?.id,
    canManagePayments,
  )
  const isPaymentModalDue = isLicensePaymentModalDue(data, now)
  const isDismissibleReminder =
    canManagePayments &&
    !isPaymentModalDue &&
    isLicensePaymentReminderDue(data, now)
  const licenseStatusRefreshKey = canManagePayments ? location.key : undefined

  useEffect(() => {
    if (!canManagePayments && !isEmployee) return
    let cancelled = false

    licenseAndPaymentApi
      .getData()
      .then((res) => {
        if (isError(res)) throw new Error()
        if (!cancelled) setData(res.data.data)
      })
      .catch((error) =>
        console.error('Ошибка загрузки статуса лицензии: ', error),
      )
    return () => {
      cancelled = true
    }
  }, [
    canManagePayments,
    isEmployee,
    licenseStatusRefreshKey,
    profile?.id,
    refreshKey,
  ])

  const isEmployeeAccessRestricted = Boolean(
    isEmployee && !canManagePayments && isPaymentModalDue,
  )
  const open = Boolean(
    (canManagePayments &&
      !isLicensePage &&
      (isPaymentModalDue || (isDismissibleReminder && !isSnoozed))) ||
      isEmployeeAccessRestricted,
  )

  const goToPayment = async () => {
    if (isOpeningPayment) return

    const paymentRole = ['admin', 'junior-admin'].includes(profile?.role ?? '')
      ? profile?.role
      : profile?.roles?.includes?.('admin')
      ? 'admin'
      : profile?.roles?.includes?.('junior-admin')
      ? 'junior-admin'
      : null

    if (!paymentRole) {
      addAlert({
        text: 'Оплата лицензии доступна только администратору платформы',
        type: 'error',
      })
      return
    }

    if (profile?.role === paymentRole) {
      history.push('/license-and-payment?tab=1')
      return
    }

    setIsOpeningPayment(true)

    try {
      const result = await usersAPI.switchRole(paymentRole)
      const user = result?.data?.user
      const token = result?.data?.token

      if (result?.status !== 200 || !user || !token) {
        throw new Error(
          result?.data?.error ||
            'Не удалось переключиться в роль администратора',
        )
      }

      if (result.data.roles) {
        user.roles = result.data.roles
      }

      AuthService.auth(user, token)
      dispatch(actionSetProfile(user))
      dispatch(actionUpdProfile({ role: paymentRole }))
      dispatch(fetchMenu())
      history.push('/license-and-payment?tab=1')
    } catch (error) {
      addAlert({
        text:
          error instanceof Error && error.message
            ? error.message
            : 'Не удалось переключиться в роль администратора',
        type: 'error',
      })
    } finally {
      setIsOpeningPayment(false)
    }
  }

  return (
    <Modal
      open={open}
      footer={null}
      centered
      width={520}
      maskClosable={isDismissibleReminder}
      keyboard={isDismissibleReminder}
      closable={isDismissibleReminder}
      onCancel={isDismissibleReminder ? remindLater : undefined}
      className={classes.modal}
      destroyOnClose
    >
      <div className={classes.content}>
        <div className={classes.iconWrapper}>
          {isEmployeeAccessRestricted ? (
            <LockOutlined
              className={classes.icon}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          ) : (
            <ClockCircleOutlined
              className={classes.icon}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          )}
        </div>
        <p className={classes.eyebrow}>
          {isEmployeeAccessRestricted
            ? 'Доступ ограничен'
            : 'Требуется продление'}
        </p>
        <h2 className={classes.title}>
          {isEmployeeAccessRestricted
            ? 'Доступ к платформе приостановлен'
            : data?.is_trial
            ? 'Пробный период завершён'
            : 'Срок действия лицензии истёк'}
        </h2>
        <p className={classes.description}>
          {isEmployeeAccessRestricted
            ? data?.is_trial
              ? 'Пробный период завершён. Вы не можете пользоваться системой. Обратитесь к руководству.'
              : 'Срок оплаты платформы истёк. Вы не можете пользоваться системой. Обратитесь к руководству.'
            : data?.is_trial
            ? 'Чтобы продолжить пользоваться возможностями платформы, выберите параметры и оплатите лицензию.'
            : isDismissibleReminder
            ? 'Продлите тариф, чтобы сохранить доступ ко всем возможностям Импульс ЛМС. Если закроете окно, мы напомним об оплате через час.'
            : 'Продлите тариф, чтобы сохранить доступ ко всем возможностям Импульс ЛМС.'}
        </p>
        {data?.expiration_date && (
          <div className={classes.dateCard}>
            <span>
              {data.is_trial ? 'Пробный период закончился' : 'Лицензия истекла'}
            </span>
            <strong>{moment(data.expiration_date).format('DD.MM.YYYY')}</strong>
          </div>
        )}
        {!isEmployeeAccessRestricted && (
          <Button
            color="primary"
            className={classes.paymentButton}
            onClick={goToPayment}
            isLoading={isOpeningPayment}
            disabled={isOpeningPayment}
          >
            Перейти к оплате
          </Button>
        )}
        {isDismissibleReminder && (
          <Button
            color="gray"
            className={classes.remindButton}
            onClick={remindLater}
          >
            Напомнить позже
          </Button>
        )}
      </div>
    </Modal>
  )
}

export default LicenseExpiredModal
